import React, { useState, useEffect } from 'react';
import { FaHistory, FaTools, FaCalendarAlt, FaCar, FaClipboardCheck, FaChartLine, FaExclamationTriangle, FaFileInvoice, FaDownload } from 'react-icons/fa';
import { IoMdWarning } from 'react-icons/io';
import moment from 'moment';
import axios from 'axios';

const ServiceHistoryTab = ({ car, selectedVehicleId, onVehicleSelect, token }) => {
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'maintenance', 'repair', 'inspection'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [serviceMetrics, setServiceMetrics] = useState({
    totalCost: 0,
    averageCost: 0,
    lastService: null,
    nextDue: null
  });
  
  useEffect(() => {
    if (car && car.vehicleInstances && car.vehicleInstances.length > 0) {
      // If no vehicle is selected, select the first one
      if (!selectedVehicleId && car.vehicleInstances[0]._id) {
        onVehicleSelect(car.vehicleInstances[0]._id);
      }
      
      // Only fetch service history if we have a selected vehicle
      if (selectedVehicleId) {
        fetchServiceHistory();
      }
    }
  }, [car, selectedVehicleId]);

  // Calculate service metrics when history changes
  useEffect(() => {
    if (serviceHistory.length > 0) {
      const totalCost = serviceHistory.reduce((sum, record) => 
        sum + (parseFloat(record.cost) || 0), 0);
      
      setServiceMetrics({
        totalCost,
        averageCost: totalCost / serviceHistory.length,
        lastService: serviceHistory.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date,
        nextDue: calculateNextDueDate(serviceHistory)
      });
    }
  }, [serviceHistory]);

  const calculateNextDueDate = (history) => {
    // Logic to calculate next due date based on service history and mileage
    // This is a placeholder - actual implementation would depend on your business rules
    if (history.length === 0) return null;
    
    const lastServiceDate = new Date(Math.max(...history.map(h => new Date(h.date))));
    const nextDueDate = new Date(lastServiceDate);
    nextDueDate.setMonth(nextDueDate.getMonth() + 6); // Assuming service is due every 6 months
    
    return nextDueDate;
  };

  const fetchServiceHistory = async () => {
    if (!car._id || !selectedVehicleId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `https://rent-server.create-site.gr/api/cars/${car._id}/vehicles/${selectedVehicleId}/service`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setServiceHistory(response.data || []);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError('Could not load service history. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if insurance is expired for a vehicle
  const isInsuranceExpired = (vehicle) => {
    if (!vehicle.insurance || !vehicle.insurance.endDate) return true;
    return moment().isAfter(moment(vehicle.insurance.endDate));
  };

  // Filter service history based on filter type
  const filteredServiceHistory = serviceHistory.filter(record => {
    if (filterType === 'all') return true;
    return record.type === filterType;
  });
  
  // Sort the filtered history
  const sortedServiceHistory = [...filteredServiceHistory].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Group service history by year
  const groupedServiceHistory = sortedServiceHistory.reduce((groups, record) => {
    const year = moment(record.date).year();
    if (!groups[year]) {
      groups[year] = [];
    }
    groups[year].push(record);
    return groups;
  }, {});
  
  // Sort years based on sort order
  const sortedYears = Object.keys(groupedServiceHistory).sort((a, b) => 
    sortOrder === 'asc' ? a - b : b - a
  );

  if (!car.vehicleInstances || car.vehicleInstances.length === 0) {
    return (
      <div className="p-6 text-center text-gray-600">
        <FaCar className="mx-auto text-4xl mb-3 text-gray-400" />
        <p>No vehicle information available for this model.</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* Service Overview */}
      {selectedVehicleId && serviceHistory.length > 0 && (
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
            <FaChartLine className="mr-2" /> 
            Service Overview
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">Last Service</p>
              <p className="text-xl font-bold mt-1 text-gray-800">
                {serviceMetrics.lastService 
                  ? moment(serviceMetrics.lastService).format('MMM D, YYYY') 
                  : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {serviceMetrics.lastService 
                  ? moment(serviceMetrics.lastService).fromNow() 
                  : 'No service records'}
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">Service Records</p>
              <p className="text-xl font-bold mt-1 text-gray-800">{serviceHistory.length}</p>
              <p className="text-xs text-gray-500 mt-1">Total records</p>
            </div>
            
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">Total Cost</p>
              <p className="text-xl font-bold mt-1 text-gray-800">
                €{serviceMetrics.totalCost.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Avg: €{serviceMetrics.averageCost.toFixed(2)}/service
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">Next Service Due</p>
              <p className="text-xl font-bold mt-1 text-gray-800">
                {serviceMetrics.nextDue 
                  ? moment(serviceMetrics.nextDue).format('MMM D, YYYY') 
                  : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {serviceMetrics.nextDue 
                  ? moment(serviceMetrics.nextDue).fromNow() 
                  : 'No data available'}
              </p>
            </div>
          </div>
        </div>
      )}
    
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaCar className="mr-2 text-blue-600" /> 
            Vehicle Selection
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {car.vehicleInstances.map(vehicle => (
              <div 
                key={vehicle._id}
                onClick={() => onVehicleSelect(vehicle._id)}
                className={`border rounded-lg p-3 cursor-pointer transition flex items-start ${
                  selectedVehicleId === vehicle._id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/30'
                }`}
              >
                <div className={`rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 ${
                  selectedVehicleId === vehicle._id 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  <FaCar />
                </div>
                
                <div className="flex-grow">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{vehicle.licensePlate}</h4>
                      <p className="text-sm text-gray-600">VIN: {vehicle.vin}</p>
                    </div>
                    {isInsuranceExpired(vehicle) && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center ml-2">
                        <IoMdWarning className="mr-1" /> Insurance Expired
                      </span>
                    )}
                  </div>
                  {vehicle.specialNotes && (
                    <p className="mt-2 text-sm text-gray-500 italic">{vehicle.specialNotes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {selectedVehicleId && (
          <div className="border-b border-gray-200">
            <div className="p-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <FaClipboardCheck className="mr-2 text-blue-600" /> Vehicle Details
              </h3>
              
              {car.vehicleInstances.map(vehicle => {
                if (vehicle._id !== selectedVehicleId) return null;
                
                const insuranceExpired = isInsuranceExpired(vehicle);
                
                return (
                  <div key={`details-${vehicle._id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold mb-3 text-gray-700 flex items-center">
                            <FaCar className="mr-2 text-blue-600" /> General Information
                          </h4>
                          <div className="space-y-2">
                            <p className="flex justify-between py-1 border-b border-gray-200">
                              <span className="text-gray-600">License Plate:</span>
                              <span className="font-medium">{vehicle.licensePlate}</span>
                            </p>
                            <p className="flex justify-between py-1 border-b border-gray-200">
                              <span className="text-gray-600">VIN:</span>
                              <span className="font-medium">{vehicle.vin}</span>
                            </p>
                            <p className="flex justify-between py-1 border-b border-gray-200">
                              <span className="text-gray-600">Condition:</span>
                              <span className="font-medium capitalize">{vehicle.condition || 'Not specified'}</span>
                            </p>
                            <p className="flex justify-between py-1 border-b border-gray-200">
                              <span className="text-gray-600">Current Mileage:</span>
                              <span className="font-medium">{vehicle.currentMileage || 'N/A'} km</span>
                            </p>
                            <p className="flex justify-between py-1">
                              <span className="text-gray-600">Purchase Date:</span>
                              <span className="font-medium">
                                {vehicle.purchaseDate 
                                  ? moment(vehicle.purchaseDate).format('DD/MM/YYYY') 
                                  : 'Not specified'}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <div className={`bg-gray-50 rounded-lg p-4 ${
                          insuranceExpired ? 'border-red-500 border-2' : ''
                        }`}>
                          <h4 className="font-semibold mb-3 text-gray-700 flex items-center justify-between">
                            <span className="flex items-center">
                              <FaFileInvoice className="mr-2 text-blue-600" /> Insurance Information
                            </span>
                            {insuranceExpired && (
                              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full flex items-center">
                                <IoMdWarning className="mr-1" /> Expired
                              </span>
                            )}
                          </h4>
                          
                          {vehicle.insurance ? (
                            <div className="space-y-2">
                              <p className="flex justify-between py-1 border-b border-gray-200">
                                <span className="text-gray-600">Provider:</span>
                                <span className="font-medium">{vehicle.insurance.provider}</span>
                              </p>
                              <p className="flex justify-between py-1 border-b border-gray-200">
                                <span className="text-gray-600">Policy Number:</span>
                                <span className="font-medium">{vehicle.insurance.policyNumber || 'N/A'}</span>
                              </p>
                              <p className="flex justify-between py-1 border-b border-gray-200">
                                <span className="text-gray-600">Insurance Type:</span>
                                <span className="font-medium capitalize">{vehicle.insurance.type || 'N/A'}</span>
                              </p>
                              <p className="flex justify-between py-1 border-b border-gray-200">
                                <span className="text-gray-600">Coverage Amount:</span>
                                <span className="font-medium">
                                  €{parseFloat(vehicle.insurance.coverageAmount).toFixed(2) || 'N/A'}
                                </span>
                              </p>
                              <p className="flex justify-between py-1 border-b border-gray-200">
                                <span className="text-gray-600">Start Date:</span>
                                <span className="font-medium">
                                  {vehicle.insurance.startDate 
                                    ? moment(vehicle.insurance.startDate).format('DD/MM/YYYY') 
                                    : 'Not specified'}
                                </span>
                              </p>
                              <p className={`flex justify-between py-1 ${insuranceExpired ? 'text-red-600 font-bold' : ''}`}>
                                <span className="text-gray-600">End Date:</span>
                                <span className="font-medium">
                                  {vehicle.insurance.endDate 
                                    ? moment(vehicle.insurance.endDate).format('DD/MM/YYYY') 
                                    : 'Not specified'}
                                </span>
                              </p>
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">Insurance information not available</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {vehicle.specialNotes && (
                      <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                        <h4 className="font-semibold mb-1 text-gray-700 flex items-center">
                          <FaExclamationTriangle className="mr-2 text-yellow-600" /> Special Notes
                        </h4>
                        <p className="text-gray-600 italic">{vehicle.specialNotes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {selectedVehicleId && (
          <div className="p-4">
            <div className="flex flex-wrap items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <FaHistory className="mr-2 text-blue-600" /> Service History
              </h3>
              
              <div className="flex items-center mt-2 sm:mt-0">
                <div className="flex items-center mr-4">
                  <label htmlFor="filter-type" className="mr-2 text-sm text-gray-600">Filter:</label>
                  <select 
                    id="filter-type"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="regular maintenance">Maintenance</option>
                    <option value="repair">Repairs</option>
                    <option value="inspection">Inspections</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <label htmlFor="sort-order" className="mr-2 text-sm text-gray-600">Sort:</label>
                  <select 
                    id="sort-order"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
                
                <button
                  className="ml-4 p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                  title="Export Service History"
                >
                  <FaDownload />
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
                <p className="mt-3 text-gray-600">Loading service records...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-r-lg">
                <p className="text-red-700 flex items-center">
                  <FaExclamationTriangle className="mr-2" /> {error}
                </p>
              </div>
            ) : sortedServiceHistory.length === 0 ? (
              <div className="text-center bg-gray-50 p-8 rounded-lg">
                <FaTools className="mx-auto text-4xl text-gray-400 mb-3" />
                <p className="text-gray-600 mb-2">No service records found for this vehicle.</p>
                <p className="text-sm text-gray-500">Regular maintenance is essential for optimal vehicle performance.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {sortedYears.map(year => (
                  <div key={year} className="relative">
                    <div className="sticky top-0 bg-white z-10 flex items-center mb-4">
                      <span className="text-lg font-semibold bg-blue-600 text-white py-1 px-4 rounded">
                        {year}
                      </span>
                      <div className="h-px bg-blue-300 flex-grow ml-4"></div>
                    </div>
                    
                    <div className="space-y-4">
                      {groupedServiceHistory[year]
                        .map((record, index) => (
                        <div key={record._id || index} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                          <div className={`px-4 py-3 flex justify-between items-center ${
                            record.type === 'regular maintenance' ? 'bg-blue-50 border-b border-blue-100' :
                            record.type === 'repair' ? 'bg-orange-50 border-b border-orange-100' :
                            record.type === 'inspection' ? 'bg-green-50 border-b border-green-100' :
                            'bg-gray-50 border-b border-gray-100'
                          }`}>
                            <div className="flex items-center">
                              <span className={`p-2 rounded-full mr-3 ${
                                record.type === 'regular maintenance' ? 'bg-blue-100 text-blue-700' :
                                record.type === 'repair' ? 'bg-orange-100 text-orange-700' :
                                record.type === 'inspection' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                <FaTools />
                              </span>
                              <div>
                                <h4 className="font-semibold">
                                  {record.type.charAt(0).toUpperCase() + record.type.slice(1)}
                                </h4>
                                <p className="text-sm text-gray-600 flex items-center">
                                  <FaCalendarAlt className="mr-1" /> 
                                  {moment(record.date).format('MMMM D, YYYY')}
                                </p>
                              </div>
                            </div>
                            
                            {record.cost && (
                              <div className="text-right">
                                <span className="font-bold">€{parseFloat(record.cost).toFixed(2)}</span>
                                <p className="text-xs text-gray-500">Service Cost</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="p-4">
                            <p className="mb-3">{record.description}</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {record.odometer && (
                                <p className="text-gray-600 bg-gray-50 p-2 rounded">
                                  <span className="font-semibold">Odometer:</span> {record.odometer} km
                                </p>
                              )}
                              
                              {record.serviceProvider && (
                                <p className="text-gray-600 bg-gray-50 p-2 rounded">
                                  <span className="font-semibold">Service Provider:</span> {record.serviceProvider}
                                </p>
                              )}
                            </div>
                            
                            {record.notes && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-gray-600 italic">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceHistoryTab;
